<template>
  <main-template>
    <!-- Header -->
    <v-row class="align-center mt-2 mb-4">
      <v-col>
        <div class="d-flex align-center">
          <v-btn color="primary" x-small text @click="goBack">
            <v-icon>fa fa-angle-left</v-icon>
          </v-btn>
          <div class="ml-2">
            <h1 class="mb-0" style="line-height: 1.2">{{ censo.descricao }}</h1>
            <div class="text-caption grey--text">Coleta de dados da Educação Básica</div>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-row v-if="loading">
      <v-col><v-progress-linear indeterminate color="primary" /></v-col>
    </v-row>

    <v-row v-else>
      <!-- Coluna principal: etapas -->
      <v-col cols="12" md="8">
        <div class="text-overline font-weight-bold mb-3 grey--text text--darken-1">
          ETAPAS DA COLETA
        </div>
        <v-row>
          <v-col v-for="(etapa, index) in censo.etapas" :key="etapa.id" cols="12" sm="6">
            <v-card outlined class="rounded-xl pa-4">
              <div class="d-flex justify-space-between align-center mb-2">
                <v-chip x-small label color="primary" class="white--text font-weight-bold">
                  ETAPA {{ index + 1 }}
                </v-chip>
                <v-chip x-small :color="etapa.situacao === 'fechada' ? 'green' : 'primary'" dark>
                  <v-icon x-small left>fa fa-circle</v-icon>
                  {{ etapa.situacao === "fechada" ? "Finalizada" : "Em coleta" }}
                </v-chip>
              </div>

              <div class="text-subtitle-1 font-weight-bold mb-1">{{ etapa.descricao }}</div>

              <v-row class="text-center mb-3" no-gutters>
                <v-col>
                  <div class="text-h6 font-weight-bold">
                    {{ etapa.modulos ? etapa.modulos.length : 0 }}
                  </div>
                  <div class="text-caption grey--text">MÓDULOS</div>
                </v-col>
              </v-row>

              <div class="d-flex justify-space-between align-center mt-2">
                <v-btn x-small text color="primary" @click="abrirEtapa(etapa)">
                  Abrir
                  <v-icon x-small right>fa fa-arrow-right</v-icon>
                </v-btn>
                <v-btn
                  x-small
                  outlined
                  color="primary"
                  :loading="gerandoArquivo === etapa.id"
                  @click.stop="gerarArquivo(etapa)"
                >
                  <v-icon x-small left>fa fa-download</v-icon>
                  Gerar arquivo
                </v-btn>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-col>

      <!-- Coluna lateral -->
      <v-col cols="12" md="4">
        <!-- Card informações -->
        <v-card outlined class="rounded-xl mb-4">
          <v-card-title class="text-subtitle-2 font-weight-bold pb-0">INFORMAÇÕES</v-card-title>
          <v-card-text>
            <v-row class="py-1" no-gutters>
              <v-col class="grey--text">Ano de referência</v-col>
              <v-col class="text-right font-weight-medium">
                {{ censo.ano ? censo.ano.descricao : "—" }}
              </v-col>
            </v-row>
            <v-divider class="my-2" />
            <v-row class="py-1" no-gutters>
              <v-col class="grey--text">Status</v-col>
              <v-col class="text-right">
                <v-chip x-small :color="censo.finalizado ? 'green' : 'orange'" dark>
                  {{ censo.finalizado ? "Finalizado" : "Aberto" }}
                </v-chip>
              </v-col>
            </v-row>
            <v-divider class="my-2" />
            <v-row class="py-1" no-gutters>
              <v-col class="grey--text">Etapas</v-col>
              <v-col class="text-right font-weight-medium">{{
                censo.etapas ? censo.etapas.length : 0
              }}</v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Card escolas exportadas -->
        <v-card outlined class="rounded-xl">
          <v-card-title class="text-subtitle-2 font-weight-bold pb-0">EXPORTAÇÕES</v-card-title>
          <v-card-text>
            <div class="text-h3 font-weight-bold primary--text text-center py-4">0</div>
            <div class="text-caption grey--text text-center mb-4">escolas com dados exportados</div>
            <v-divider class="mb-3" />
            <v-row no-gutters>
              <v-col class="grey--text text-caption">Pendentes</v-col>
              <v-col class="text-right orange--text font-weight-medium">—</v-col>
            </v-row>
            <v-row no-gutters class="mt-1">
              <v-col class="grey--text text-caption">Validadas</v-col>
              <v-col class="text-right primary--text font-weight-medium">—</v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Painel de pendências (bloqueia a geração do arquivo) -->
    <v-dialog v-model="dialogPendencias" max-width="760" scrollable>
      <v-card v-if="pendencias">
        <v-card-title class="text-h6">
          <v-icon color="error" class="mr-2">error_outline</v-icon>
          Pendências do censo
        </v-card-title>
        <v-card-subtitle class="pt-2">
          {{ pendencias.etapaNome }} — {{ pendencias.totalErros }} pendência(s). Corrija antes de
          gerar o arquivo.
        </v-card-subtitle>
        <v-divider />
        <v-card-text style="max-height: 60vh">
          <div v-if="pendencias.regrasEtapa && pendencias.regrasEtapa.length" class="mb-4">
            <div class="text-subtitle-2 font-weight-bold mb-2">Regras da etapa</div>
            <v-alert
              v-for="(regra, i) in pendencias.regrasEtapa"
              :key="`r-${i}`"
              type="error"
              dense
              text
              class="mb-2 text-body-2"
            >
              {{ regra.msg || regra }}
            </v-alert>
          </div>

          <div v-for="modulo in pendencias.modulos" :key="modulo.codigo" class="mb-4">
            <div class="text-subtitle-2 font-weight-bold mb-1">
              R{{ modulo.codigo }} — {{ modulo.nome }}
            </div>
            <v-expansion-panels accordion flat>
              <v-expansion-panel
                v-for="(ent, j) in modulo.entidades"
                :key="`${modulo.codigo}-${j}`"
              >
                <v-expansion-panel-header class="py-1">
                  <span class="text-body-2">
                    {{ ent.nome }}
                    <v-chip x-small color="error" outlined class="ml-2">
                      {{ ent.erros.length }}
                    </v-chip>
                  </span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <ul class="text-body-2 grey--text text--darken-2 pl-4">
                    <li v-for="(erro, k) in ent.erros" :key="k" class="mb-1">{{ erro }}</li>
                  </ul>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            outlined
            :loading="gerandoRelatorio"
            @click="gerarRelatorioPendencias"
          >
            <v-icon left small>fa fa-file-pdf</v-icon>
            Gerar relatório
          </v-btn>
          <v-btn text @click="dialogPendencias = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </main-template>
</template>

<script>
export default {
  data() {
    return {
      censo: { etapas: [] },
      loading: true,
      gerandoArquivo: null,
      pendencias: null,
      dialogPendencias: false,
      gerandoRelatorio: false,
    };
  },

  async created() {
    await this.buscarCensos();
  },

  methods: {
    goBack() {
      this.$router.push({ name: "censo-escolar" });
    },

    abrirEtapa(etapa) {
      this.$router.push({
        name: "censo-escolar-etapa",
        params: { id: this.$route.params.id, etapaId: etapa.id },
      });
    },

    async gerarArquivo(etapa) {
      this.gerandoArquivo = etapa.id;
      try {
        // Valida a etapa inteira antes de gerar. Se houver pendências, abre o
        // painel e NÃO gera (evita arquivo que o INEP rejeitaria).
        const relatorio = await this.$services.censoService.validarEtapa(etapa.id);
        if (!relatorio.valido) {
          this.pendencias = {
            ...relatorio,
            etapaId: etapa.id,
            etapaNome: etapa.descricao || `Etapa ${etapa.id}`,
          };
          this.dialogPendencias = true;
          this.$toast.danger(
            `Existem ${relatorio.totalErros} pendência(s) — corrija antes de gerar.`
          );
          return;
        }
        await this.$services.censoService.gerarArquivoCenso(etapa.id);
        this.$toast.success("Arquivo gerado com sucesso.");
      } catch {
        this.$toast.danger("Erro ao gerar o arquivo.");
      } finally {
        this.gerandoArquivo = null;
      }
    },

    async gerarRelatorioPendencias() {
      if (!this.pendencias) return;
      this.gerandoRelatorio = true;
      try {
        await this.$services.censoService.relatorioPendencias(this.pendencias.etapaId);
      } catch {
        this.$toast.danger("Erro ao gerar o relatório.");
      } finally {
        this.gerandoRelatorio = false;
      }
    },

    async buscarCensos() {
      try {
        this.censo = await this.$services.censoService.show(this.$route.params.id);
      } catch (error) {
        this.$toast.danger("Erro ao carregar censo.");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
